import React, { useEffect, useRef, useState } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import * as d3 from 'd3';
import { Box, IconButton, Paper, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import {
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  BarChart as BarChartIcon,
  ShowChart as LineChartIcon,
  PieChart as PieChartIcon,
  GridOn as HeatmapIcon,
  DonutLarge as DonutIcon
} from '@mui/icons-material';

const CHART_TYPES = {
  bar: { label: 'Bar Chart', icon: <BarChartIcon /> },
  line: { label: 'Line Chart', icon: <LineChartIcon /> },
  pie: { label: 'Pie Chart', icon: <PieChartIcon /> },
  donut: { label: 'Donut Chart', icon: <DonutIcon /> },
  heatmap: { label: 'Heatmap', icon: <HeatmapIcon /> }
};

// eslint-disable-next-line max-lines-per-function
const D3ChartComponent = ({ node, updateAttributes, deleteNode, editor }) => {
  const chartRef = useRef(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const { chartType, chartData, chartOptions, chartId } = node.attrs;

  useEffect(() => {
    if (chartRef.current && chartData.length > 0) {
      drawChart();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartType, chartData, chartOptions]);

  const drawChart = () => {
    // Clear existing chart
    d3.select(chartRef.current).selectAll('*').remove();

    const margin = { top: 40, right: 80, bottom: 60, left: 60 };
    const width = typeof chartOptions.width === 'string' ?
      chartRef.current.offsetWidth : chartOptions.width;
    const height = chartOptions.height - margin.top - margin.bottom;

    const svg = d3.select(chartRef.current)
      .append('svg')
      .attr('width', '100%')
      .attr('height', chartOptions.height)
      .attr('viewBox', `0 0 ${width} ${chartOptions.height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet');

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Add title
    if (chartOptions.title) {
      svg.append('text')
        .attr('x', width / 2)
        .attr('y', margin.top / 2)
        .attr('text-anchor', 'middle')
        .style('font-size', '16px')
        .style('font-weight', 'bold')
        .text(chartOptions.title);
    }

    switch (chartType) {
      case 'bar':
        drawBarChart(g, width - margin.left - margin.right, height);
        break;
      case 'line':
        drawLineChart(g, width - margin.left - margin.right, height);
        break;
      case 'pie':
        drawPieChart(svg, width, chartOptions.height);
        break;
      case 'donut':
        drawDonutChart(svg, width, chartOptions.height);
        break;
      case 'heatmap':
        drawHeatmap(g, width - margin.left - margin.right, height);
        break;
      default:
        drawBarChart(g, width - margin.left - margin.right, height);
    }
  };

  const drawBarChart = (g, width, height) => {
    const x = d3.scaleBand()
      .domain(chartData.map(d => d.label || d.x))
      .range([0, width])
      .padding(chartOptions.padding || 0.1);

    const y = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.value || d.y)])
      .nice()
      .range([height, 0]);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .append('text')
      .attr('x', width / 2)
      .attr('y', 40)
      .attr('fill', 'black')
      .style('text-anchor', 'middle')
      .text(chartOptions.xLabel || '');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y))
      .append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -40)
      .attr('x', -height / 2)
      .attr('fill', 'black')
      .style('text-anchor', 'middle')
      .text(chartOptions.yLabel || '');

    // Bars
    g.selectAll('.bar')
      .data(chartData)
      .enter().append('rect')
      .attr('class', 'bar')
      .attr('x', d => x(d.label || d.x))
      .attr('width', x.bandwidth())
      .attr('y', d => y(d.value || d.y))
      .attr('height', d => height - y(d.value || d.y))
      .attr('fill', (d, i) => chartOptions.colors[i % chartOptions.colors.length]);

    // Tooltips
    if (chartOptions.showTooltip) {
      const tooltip = d3.select('body').append('div')
        .attr('class', 'd3-tooltip')
        .style('position', 'absolute')
        .style('visibility', 'hidden')
        .style('background', 'rgba(0, 0, 0, 0.8)')
        .style('color', 'white')
        .style('padding', '8px')
        .style('border-radius', '4px')
        .style('font-size', '12px');

      g.selectAll('.bar')
        .on('mouseover', function(event, d) {
          tooltip.style('visibility', 'visible')
            .text(`${d.label || d.x}: ${d.value || d.y}`);
        })
        .on('mousemove', function(event) {
          tooltip.style('top', (event.pageY - 10) + 'px')
            .style('left', (event.pageX + 10) + 'px');
        })
        .on('mouseout', function() {
          tooltip.style('visibility', 'hidden');
        });
    }
  };

  const drawLineChart = (g, width, height) => {
    const x = d3.scaleLinear()
      .domain(d3.extent(chartData, (d, i) => i))
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([0, d3.max(chartData, d => d.value || d.y)])
      .nice()
      .range([height, 0]);

    const line = d3.line()
      .x((d, i) => x(i))
      .y(d => y(d.value || d.y))
      .curve(d3.curveMonotoneX);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x));

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y));

    // Line
    g.append('path')
      .datum(chartData)
      .attr('fill', 'none')
      .attr('stroke', chartOptions.colors[0])
      .attr('stroke-width', 2)
      .attr('d', line);

    // Dots
    g.selectAll('.dot')
      .data(chartData)
      .enter().append('circle')
      .attr('class', 'dot')
      .attr('cx', (d, i) => x(i))
      .attr('cy', d => y(d.value || d.y))
      .attr('r', 4)
      .attr('fill', chartOptions.colors[0]);
  };

  const drawPieChart = (svg, width, height) => {
    const radius = Math.min(width, height) / 2 - 40;
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    const pie = d3.pie()
      .value(d => d.value || d.y)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(0)
      .outerRadius(radius);

    const arcs = g.selectAll('.arc')
      .data(pie(chartData))
      .enter().append('g')
      .attr('class', 'arc');

    arcs.append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => chartOptions.colors[i % chartOptions.colors.length]);

    // Labels
    if (chartOptions.showLegend) {
      arcs.append('text')
        .attr('transform', d => `translate(${arc.centroid(d)})`)
        .attr('text-anchor', 'middle')
        .style('font-size', '12px')
        .text(d => d.data.label || d.data.x);
    }
  };

  const drawDonutChart = (svg, width, height) => {
    const radius = Math.min(width, height) / 2 - 40;
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2},${height / 2})`);

    const pie = d3.pie()
      .value(d => d.value || d.y)
      .sort(null);

    const arc = d3.arc()
      .innerRadius(radius * 0.5)
      .outerRadius(radius);

    const arcs = g.selectAll('.arc')
      .data(pie(chartData))
      .enter().append('g')
      .attr('class', 'arc');

    arcs.append('path')
      .attr('d', arc)
      .attr('fill', (d, i) => chartOptions.colors[i % chartOptions.colors.length]);

    // Center text
    const total = d3.sum(chartData, d => d.value || d.y);
    g.append('text')
      .attr('text-anchor', 'middle')
      .style('font-size', '24px')
      .style('font-weight', 'bold')
      .text(total);
  };

  const drawHeatmap = (g, width, height) => {
    // Simple heatmap implementation
    const xLabels = [...new Set(chartData.map(d => d.x))];
    const yLabels = [...new Set(chartData.map(d => d.y))];

    const x = d3.scaleBand()
      .domain(xLabels)
      .range([0, width])
      .padding(0.05);

    const y = d3.scaleBand()
      .domain(yLabels)
      .range([0, height])
      .padding(0.05);

    const colorScale = d3.scaleSequential()
      .interpolator(d3.interpolateInferno)
      .domain([0, d3.max(chartData, d => d.value)]);

    g.selectAll('.cell')
      .data(chartData)
      .enter().append('rect')
      .attr('x', d => x(d.x))
      .attr('y', d => y(d.y))
      .attr('width', x.bandwidth())
      .attr('height', y.bandwidth())
      .attr('fill', d => colorScale(d.value));
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX - 2,
      mouseY: event.clientY - 4,
    });
  };

  const handleClose = () => {
    setContextMenu(null);
  };

  const handleConfigure = () => {
    // This will open the configuration modal
    editor.chain().focus().command(({ tr, _dispatch }) => {
      const { doc, selection } = tr;
      const pos = selection.$from.pos;
      const node = doc.nodeAt(pos);

      if (node && node.type.name === 'd3Chart') {
        // Dispatch custom event to open configuration modal
        window.dispatchEvent(new CustomEvent('configure-chart', {
          detail: {
            nodePos: pos,
            chartType: node.attrs.chartType,
            chartData: node.attrs.chartData,
            chartOptions: node.attrs.chartOptions,
            updateAttributes
          }
        }));
      }
      return true;
    }).run();
    handleClose();
  };

  const handleDelete = () => {
    deleteNode();
    handleClose();
  };

  const handleChangeType = (newType) => {
    updateAttributes({ chartType: newType });
    handleClose();
  };

  return (
    <NodeViewWrapper
      className="d3-chart-node"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Paper
        elevation={isHovered ? 3 : 1}
        sx={{
          position: 'relative',
          p: 2,
          border: '1px solid',
          borderColor: isHovered ? 'primary.main' : 'divider',
          transition: 'all 0.2s'
        }}
        onContextMenu={handleContextMenu}
      >
        {/* Chart controls visible on hover */}
        {isHovered && (
          <Box sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'flex',
            gap: 1,
            zIndex: 10
          }}>
            <Tooltip title="Configure Chart">
              <IconButton size="small" onClick={handleConfigure}>
                <SettingsIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Chart">
              <IconButton size="small" onClick={handleDelete} color="error">
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}

        {/* Chart container */}
        <div
          ref={chartRef}
          id={chartId}
          style={{
            width: '100%',
            minHeight: chartOptions.height || 400,
            cursor: 'pointer'
          }}
        />

        {/* No data placeholder */}
        {(!chartData || chartData.length === 0) && (
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            color: 'text.secondary'
          }}>
            <Typography variant="h6">No Data</Typography>
            <Typography variant="body2">
              Right-click to configure this chart
            </Typography>
          </Box>
        )}

        <NodeViewContent />
      </Paper>

      {/* Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleClose}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleConfigure}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Configure Chart</ListItemText>
        </MenuItem>
        <MenuItem>
          <ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Change Chart Type</ListItemText>
          <Menu
            anchorEl={contextMenu}
            open={Boolean(contextMenu)}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            {Object.entries(CHART_TYPES).map(([type, info]) => (
              <MenuItem key={type} onClick={() => handleChangeType(type)}>
                <ListItemIcon>{info.icon}</ListItemIcon>
                <ListItemText>{info.label}</ListItemText>
              </MenuItem>
            ))}
          </Menu>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Delete Chart</ListItemText>
        </MenuItem>
      </Menu>
    </NodeViewWrapper>
  );
};

export default D3ChartComponent;